import _extend from 'lodash/extend';
import _isArray from 'lodash/isArray';
import _isNumber from 'lodash/isNumber';

import addAdmissionGroupTemplate from 'panels/addAdmissionGroup/template.html';
import confirmationModalTemplate from 'modals/confirmation/template.html';
import confirmationInventoryModalTemplate from 'modals/confirmationInventory/template.html';
import confirmationEventModalTemplate from 'modals/confirmationEvent/template.html';
import migrateInventoryModalTemplate from 'modals/migrateInventory/template.html';
import acceptTermsModalTemplate from 'modals/acceptTerms/template.html';
import inventoryQrCodeModalTemplate from 'modals/inventoryQrCode/template.html';
import addInventoryPanelTemplate from 'panels/addInventory/template.html';
import addIntegrationPanelTemplate from 'panels/addIntegration/template.html';
import admissionGroupNotVisibleTemplate from 'modals/admissionGroupNotVisible/template.html';
import bulkAddAdmissionGroupTemplate from 'modals/bulkAddAdmissionGroups/template.html';
import addVenueLayoutTemplate from 'modals/addVenueLayout/template.html';
import mergeCustomersTemplate from 'modals/mergeCustomers/template.html';
import mergeAttributionsTemplate from 'modals/mergeAttributions/template.html';
import admissionGroupCommissionsTemplate from 'modals/admissionGroupCommissions/template.html';
import editScheduledReportTemplate from 'modals/editScheduledReport/template.html';
import attributionLinksEmailTemplate from 'modals/attributionLinksEmail/template.html';
import confirmationPaymentRequestNotificationModalTemplate from 'modals/confirmationPaymentRequestNotification/template.html';

function ModalServiceFn($uibModal, $q, angularSlideOutPanel) {
  class ModalService {
    /**
      @param {Object} options
      @param {String} options.modalMessage - Message to diplay in the modal
      @param {String} [options.modalHeader] - Value to use as the modal's header
      @param {String} [options.confirmBtnTxt] - Value to use as the modal's confirm button text
      @param {String} [options.confirmingBtnTxt] - Value to use as the modal's confirm button text while saving
      @param {String} [options.cancelBtnTxt] - Value to use as the modal's cancel button text
      @return Promise
    */
    confirmation(options) { //eslint-disable-line
      if (!options) throw new Error('modalService - confirmation() - options is required');
      if (!options.modalMessage) throw new Error('modalService - confirmation() - options.modalMessage is required');

      const deferred = $q.defer();

      const modalInstance = $uibModal.open({
        template: confirmationModalTemplate,
        controller: 'confirmationModalCtrl',
        size: options.size || 'sm',
        resolve: {
          modalHeader: function() {
            return options.modalHeader || null;
          },
          modalMessage: function() {
            return options.modalMessage || null;
          },
          confirmingBtnClass: function() {
            return options.confirmingBtnClass || null;
          },
          confirmBtnTxt: function() {
            return options.confirmBtnTxt || null;
          },
          confirmingBtnTxt: function() {
            return options.confirmingBtnTxt || null;
          },
          hideCancelBtn: function() {
            return options.hideCancelBtn || false;
          },
          cancelBtnTxt: function() {
            return options.cancelBtnTxt || null;
          },
          deferred: function() {
            return deferred;
          }
        }
      });

      return $q((resolve, reject) => {
        deferred.promise
          .then(result => {
            if (result === false) {
              return modalInstance.close(null);
            }

            return resolve({
              confirm: result,
              modalInstance: modalInstance
            });
          })
          .catch(_catchModalError(reject));
      });
    }

    /**
      @param {Object} options
      @param {String} options.modalMessage - Message to diplay in the modal
      @param {String} [options.modalHeader] - Value to use as the modal's header
      @param {String} [options.confirmBtnTxt] - Value to use as the modal's confirm button text
      @param {String} [options.confirmingBtnTxt] - Value to use as the modal's confirm button text while saving
      @param {String} [options.cancelBtnTxt] - Value to use as the modal's cancel button text
      @return Promise
    */
    confirmationInventory(options) { //eslint-disable-line
      if (!options) throw new Error('modalService - confirmation() - options is required');

      const deferred = $q.defer();

      const modalInstance = $uibModal.open({
        template: confirmationInventoryModalTemplate,
        controller: 'confirmationInventoryModalCtrl',
        size: options.size || 'sm',
        resolve: {
          deferred: function() {
            return deferred;
          },
          deleteMode: function() {
            return options.deleteMode;
          }
        }
      });

      return $q((resolve, reject) => {
        deferred.promise
          .then(result => {
            if (result === false) {
              return modalInstance.close(null);
            }

            return resolve({
              confirm: result,
              modalInstance: modalInstance
            });
          })
          .catch(_catchModalError(reject));
      });
    }

    /**
      @param {Object} options
      @param {String} options.modalMessage - Message to diplay in the modal
      @param {String} [options.modalHeader] - Value to use as the modal's header
      @param {String} [options.confirmBtnTxt] - Value to use as the modal's confirm button text
      @param {String} [options.confirmingBtnTxt] - Value to use as the modal's confirm button text while saving
      @param {String} [options.cancelBtnTxt] - Value to use as the modal's cancel button text
      @return Promise
    */
    confirmationEvent(options) { //eslint-disable-line
      if (!options) throw new Error('modalService - confirmation() - options is required');

      const deferred = $q.defer();

      const modalInstance = $uibModal.open({
        template: confirmationEventModalTemplate,
        controller: 'confirmationEventModalCtrl',
        size: options.size || 'sm',
        resolve: {
          deferred: function() {
            return deferred;
          },
          deleteMode: function() {
            return options.deleteMode;
          }
        }
      });

      return $q((resolve, reject) => {
        deferred.promise
          .then(result => {
            if (result === false) {
              return modalInstance.close(null);
            }

            return resolve({
              confirm: result,
              modalInstance: modalInstance
            });
          })
          .catch(_catchModalError(reject));
      });
    }

    migrateInventory(options) { //eslint-disable-line
      if (!options) throw new Error('modalService - confirmation() - options is required');

      const deferred = $q.defer();

      const modalInstance = $uibModal.open({
        template: migrateInventoryModalTemplate,
        controller: 'migrateInventoryModalCtrl',
        size: options.size || 'sm',
        resolve: {
          deferred: function() {
            return deferred;
          },
          inventory: function() {
            return options.inventory;
          }
        }
      });

      return $q((resolve, reject) => {
        deferred.promise
          .then(result => {
            if (result === false) {
              return modalInstance.close(null);
            }

            return resolve(_extend({
              modalInstance: modalInstance
            }, result));
          })
          .catch(_catchModalError(reject));
      });
    }

    /**
      @param {Object} options
      @param {String} options.modalMessage - Message to diplay in the modal
      @param {String} [options.email]
      @return Promise
    */
    confirmationPaymentRequest(options) { //eslint-disable-line
      if (!options) throw new Error('modalService - confirmation() - options is required');
      if (!options.paymentRequest) throw new Error('modalService - confirmation() - options.paymentRequest is required');

      const deferred = $q.defer();

      const modalInstance = $uibModal.open({
        template: confirmationPaymentRequestNotificationModalTemplate,
        controller: 'confirmationPaymentRequestNotificationModalCtrl',
        size: options.size || 'sm',
        resolve: {
          paymentRequest: function() {
            return options.paymentRequest;
          },
          email: function() {
            return options.email || null;
          },
          deferred: function() {
            return deferred;
          }
        }
      });

      return $q((resolve, reject) => {
        deferred.promise
          .then(result => {
            if (result === false) {
              return modalInstance.close(null);
            }

            return resolve({
              confirm: result,
              modalInstance: modalInstance
            });
          })
          .catch(_catchModalError(reject));
      });
    }

    /**
      @return Promise
    */
    acceptTerms() { //eslint-disable-line
      const deferred = $q.defer();

      const modalInstance = $uibModal.open({
        template: acceptTermsModalTemplate,
        controller: 'acceptTermsModalCtrl',
        size: 'sm',
        backdrop: 'static',
        keyboard: false
      });

      return $q((resolve, reject) => {
        deferred.promise
          .then(result => {
            if (result === false) {
              return modalInstance.close(null);
            }

            return resolve({
              modalInstance: modalInstance
            });
          })
          .catch(_catchModalError(reject));
      });
    }

    /**
      @return Promise
    */
    inventoryQrCode(options) { //eslint-disable-line
      if (!options) throw new Error('options');

      const deferred = $q.defer();

      const modalInstance = $uibModal.open({
        template: inventoryQrCodeModalTemplate,
        controller: 'inventoryQrCodeModalCtrl',
        size: 'sm',
        backdrop: 'static',
        keyboard: false,
        resolve: {
          inventory: function() {
            return options.inventory || null;
          }
        }
      });

      return $q((resolve, reject) => {
        deferred.promise
          .then(result => {
            if (result === false) {
              return modalInstance.close(null);
            }

            return resolve({
              modalInstance: modalInstance
            });
          })
          .catch(_catchModalError(reject));
      });
    }

    /**
      @return Promise
    */
    attributionLinksEmail(options) { //eslint-disable-line
      const deferred = $q.defer();

      const modalInstance = $uibModal.open({
        template: attributionLinksEmailTemplate,
        controller: 'attributionLinksEmailModalCtrl',
        size: 'md',
        backdrop: 'static',
        keyboard: true,
        resolve: {
          modalHeader: function() {
            return options.modalHeader || null;
          },
          event: function() {
            return options.event || null;
          },
          attributionSources: function() {
            return options.attributionSources || null;
          }
        }
      });

      return $q((resolve, reject) => {
        deferred.promise
          .then(result => {
            if (result === false) {
              return modalInstance.close(null);
            }

            return resolve({
              modalInstance: modalInstance
            });
          })
          .catch(_catchModalError(reject));
      });
    }

    /**
      @param {Object} options
      @param {Object} options.inventory
      @return Promise
    */
    addOrUpdateInventory(options) { //eslint-disable-line
      options = options || {};

      const modalInstance = angularSlideOutPanel.open({
        template: addInventoryPanelTemplate,
        controller: 'addInventoryPanelCtrl',
        openOn: 'right',
        panelClass: 'add-inventory-panel-bg',
        backdrop: 'static',
        resolve: {
          date: function() {
            return options.date || null;
          },
          day: function() {
            return _isNumber(options.day) ? options.day : null;
          },
          inventory: function() {
            return options.inventory || null;
          },
          venue: function() {
            return options.venue || null;
          },
          schedule: function() {
            return options.schedule || null;
          },
          type: function() {
            return options.type || null;
          },
          enable: function() {
            return options.enable;
          },
          onAddedOrUpdated: function() {
            return options.onAddedOrUpdated || null;
          }
        }
      });

      return $q((resolve, reject) => {
        modalInstance.result
          .then(result => {
            if (result === false) {
              return modalInstance.close(null);
            }

            return resolve(result);
          })
          .catch(_catchModalError(reject));
      });
    }

    /**
      @param {Object} options
      @param {String} options.type - the type of Addmission Group you want to create
      @param {String} options.venueId
      @param {String} options.organizationId
      @param {String} [options.venueId]
      @param {Object} [options.admissionGroup] - an existing Admission Group object - puts this directive into Edit mode
      @param {Object} [options.customer] - an existing Customer object to create the Admission Group with
      @param {String} [options.size]
      @return Promise
    */
    addAdmissionGroup(options) { //eslint-disable-line
      if (!options) throw new Error('modalService - addAdmissionGroup() - options is required');
      if (!options.type) throw new Error('modalService - addAdmissionGroup() - options.type is required');
      if (!options.venue) throw new Error('modalService - addAdmissionGroup() - options.venue is required');
      if (!options.organizationId) throw new Error('modalService - addAdmissionGroup() - options.organizationId is required');

      if (options.date) {
        if (_isNumber(options.date)) options.date = new Date(options.date);
      }

      const modalInstance = angularSlideOutPanel.open({
        template: addAdmissionGroupTemplate,
        controller: 'addAdmissionGroupPanelCtrl',
        openOn: 'right',
        panelClass: 'add-admission-group-panel-bg',
        backdrop: 'static',
        resolve: {
          admissionGroupType: function() {
            return options.type;
          },
          admissionGroup: function() {
            return options.admissionGroup || null;
          },
          customer: function() {
            return options.customer || null;
          },
          venue: function() {
            return options.venue || null;
          },
          eventId: function() {
            return options.eventId || null;
          },
          events: function() {
            return options.events || null;
          },
          organizationId: function() {
            return options.organizationId || null;
          },
          date: function() {
            return options.date || new Date();
          },
          onAddedOrUpdated: function() {
            return options.onAddedOrUpdated || null;
          },
          currentTab: function() {
            return options.currentTab || null;
          }
        }
      });

      return $q((resolve, reject) => {
        modalInstance.result
          .then(result => {
            if (result === false) {
              return modalInstance.close(null);
            }

            return resolve(result);
          })
          .catch(_catchModalError(reject));
      });
    }

    /**
      @param {Object} options
      @param {String} [options.modalHeader] - Value to use as the modal's header
      @param {Function} [options.onBtnClick]
      @return Promise
    */
    admissionGroupNotVisible(options) { //eslint-disable-line
      if (!options) throw new Error('modalService - admissionGroupNotVisible() - options is required');

      const modalInstance = $uibModal.open({
        template: admissionGroupNotVisibleTemplate,
        controller: 'admissionGroupNotVisibleModalCtrl',
        size: options.size || 'sm',
        backdrop: false,
        windowClass: 'admission-group-not-visible-modal-window',
        resolve: {
          modalHeader: function() {
            return options.modalHeader || null;
          },
          onBtnClick: function() {
            return options.onBtnClick || null;
          }
        }
      });

      return $q((resolve, reject) => {
        modalInstance.result
          .then(result => {
            if (result === false) {
              return modalInstance.close(null);
            }

            return resolve(result);
          })
          .catch(_catchModalError(reject));
      });
    }

    /**
      @param {Object} options
      @param {Object} options.inventory
      @return Promise
    */
    addIntegration(options) { //eslint-disable-line
      options = options || {};

      const modalInstance = angularSlideOutPanel.open({
        template: addIntegrationPanelTemplate,
        controller: 'addIntegrationPanelCtrl',
        openOn: 'right',
        panelClass: 'add-integration-panel-bg',
        backdrop: 'static',
        resolve: {
          integration: function() {
            return options.integration || null;
          },
          integrationType: function() {
            return options.integrationType || null;
          },
          venue: function() {
            return options.venue || null;
          },
          onSuccess: function() {
            return options.onSuccess || null;
          }
        }
      });

      return $q((resolve, reject) => {
        modalInstance.result
          .then(result => {
            if (result === false) {
              return modalInstance.close(null);
            }

            return resolve(result);
          })
          .catch(_catchModalError(reject));
      });
    }

    /**
      @param {Object} options
      @param {String} options.type - the type of Addmission Group you want to create
      @param {String} options.venueId
      @param {String} options.organizationId
      @param {String} [options.venueId]
      @param {Array} [options.inventory]
      @param {String} [options.modalHeader] - Value to use as the modal's header
      @param {String} [options.size]
      @return Promise
    */
    bulkAddAdmissionGroup(options) { //eslint-disable-line
      if (!options) throw new Error('modalService - bulkAddAdmissionGroups() - options is required');
      if (!options.type) throw new Error('modalService - bulkAddAdmissionGroups() - options.type is required');
      if (!options.venue) throw new Error('modalService - bulkAddAdmissionGroups() - options.venue is required');
      if (!options.organizationId) throw new Error('modalService - bulkAddAdmissionGroups() - options.organizationId is required');

      if (options.date) {
        if (_isNumber(options.date)) options.date = new Date(options.date);
      }

      const modalInstance = $uibModal.open({ //eslint-disable-line
        template: bulkAddAdmissionGroupTemplate,
        controller: 'bulkAddAdmissionGroupsModalCtrl',
        size: options.size || 'md',
        resolve: {
          modalHeader: function() {
            return options.modalHeader || null;
          },
          admissionGroupType: function() {
            return options.type;
          },
          venue: function() {
            return options.venue || null;
          },
          organizationId: function() {
            return options.organizationId || null;
          },
          eventId: function() {
            return options.eventId || null;
          },
          date: function() {
            return options.date || new Date();
          },
          inventory: function() {
            return options.inventory || new Date();
          },
          bulkUploadType: function() {
            return options.bulkUploadType || null;
          }
        }
      });

      return $q((resolve, reject) => {
        modalInstance.result
          .then(result => {
            if (result === false) {
              return modalInstance.close(null);
            }

            return resolve(result);
          })
          .catch(_catchModalError(reject));
      });
    }

    /**
      @desc Add and update a venue layout
      @param {Object} [options]
      @param {Object} [options.venueLayout] - venue layout object to update, otherwise it creates a new venueLayout
      @param {String} [options.modalHeader] - Value to use as the modal's header
      @return Promise
    */
    addVenueLayout(options) { //eslint-disable-line
      options = options || {};

      const modalInstance = $uibModal.open({ //eslint-disable-line
        template: addVenueLayoutTemplate,
        controller: 'addVenueLayoutModalCtrl',
        size: options.size || 'sm',
        resolve: {
          venueId: function() {
            return options.venueId || null;
          },
          venueLayout: function() {
            return options.venueLayout || null;
          },
          modalHeader: function() {
            return options.modalHeader || 'Add Floorplan';
          }
        }
      });

      return $q((resolve, reject) => {
        modalInstance.result
          .then(result => {
            if (result === false) {
              return modalInstance.close(null);
            }

            return resolve(result);
          })
          .catch(_catchModalError(reject));
      });
    }

    /**
      @desc Merge customers
      @param {Object} options
      @param {Object} options.customers - array of customer objects to merge
      @param {String} [options.modalHeader] - Value to use as the modal's header
      @return Promise
    */
    mergeCustomers(options) { //eslint-disable-line
      if (!options) throw new Error('modalService - mergeCustomers() - options is required');
      if (!options.customers || !_isArray(options.customers)) throw new Error('modalService - mergeCustomers() - options is required');

      const modalInstance = $uibModal.open({ //eslint-disable-line
        template: mergeCustomersTemplate,
        controller: 'mergeCustomersModalCtrl',
        size: options.size || 'lg',
        resolve: {
          customers: function() {
            return options.customers;
          },
          venueId: function() {
            return options.venueId;
          },
          modalHeader: function() {
            return options.modalHeader || 'Merge Customers';
          }
        }
      });

      return $q((resolve, reject) => {
        modalInstance.result
          .then(result => {
            if (result === false) {
              return modalInstance.close(null);
            }

            return resolve(result);
          })
          .catch(_catchModalError(reject));
      });
    }

    /**
      @desc Merge customers
      @param {Object} options
      @param {Object} options.attributions - array of attribution objects to merge
      @return Promise
    */
    mergeAttributions(options) { //eslint-disable-line
      if (!options) throw new Error('modalService - mergeAttributions() - options is required');
      if (!options.attributions || !_isArray(options.attributions)) throw new Error('modalService - mergeAttributions() - options is required');

      const modalInstance = $uibModal.open({
        template: mergeAttributionsTemplate,
        controller: 'mergeAttributionsModalCtrl',
        size: options.size || 'lg',
        resolve: {
          attributions: function() {
            return options.attributions;
          },
          modalHeader: function() {
            return options.modalHeader || 'Merge Booked Bys';
          }
        }
      });

      return $q((resolve, reject) => {
        modalInstance.result
          .then(result => {
            if (result === false) {
              return modalInstance.close(null);
            }

            return resolve(result);
          })
          .catch(_catchModalError(reject));
      });
    }

    /**
      @desc Update commissions on a group of admission groups
      @param {Object} options
      @param {Array} options.admissionGroups
      @param {String} [options.modalHeader] - Value to use as the modal's header
      @return Promise
    */
    admissionGroupCommissions(options) { //eslint-disable-line
      if (!options) throw new Error('modalService - admissionGroupCommissions() - options is required');
      if (!options.admissionGroups) throw new Error('modalService - admissionGroupCommissions() - options.admissionGroups is required');
      if (!_isArray(options.admissionGroups)) throw new Error('modalService - admissionGroupCommissions() - options.admissionGroups must be an array');

      const modalInstance = $uibModal.open({
        template: admissionGroupCommissionsTemplate,
        controller: 'admissionGroupCommissionsModalCtrl',
        size: options.size || 'lg',
        resolve: {
          admissionGroups: function() {
            return options.admissionGroups || null;
          },
          modalHeader: function() {
            return options.modalHeader || 'Reservations';
          }
        }
      });

      return $q((resolve, reject) => {
        modalInstance.result
          .then(result => {
            if (result === false) {
              return modalInstance.close(null);
            }

            return resolve(result);
          })
          .catch(_catchModalError(reject));
      });
    }

    /**
      @desc Edit a schedule dreport.
      @param {Object} [options]
      @param {Object} [options.report] - Report to edit
      @return Promise
    */
    editScheduledReport(options) { //eslint-disable-line
      options = options || {};

      const modalInstance = $uibModal.open({
        template: editScheduledReportTemplate,
        controller: 'editScheduledReportModalCtrl',
        size: 'sm',
        resolve: {
          report: function() {
            return options.report || null;
          },
          venueId: function() {
            return options.venueId || null;
          }
        }
      });

      return $q((resolve, reject) => {
        modalInstance.result
          .then(result => {
            if (result === false) {
              return modalInstance.close(null);
            }

            return resolve(result);
          })
          .catch(_catchModalError(reject));
      });
    }
  }

  /**
   * @param {Function} reject
   */
  function _catchModalError(reject) {
    return function(error) {
      if (error && (error === 'backdrop click' || error === 'escape key press')) return; //just eat these because they aren't really errors

      reject(error);
    };
  }

  return new ModalService();
}

ModalServiceFn.$inject = [
  '$uibModal',
  '$q',
  'angularSlideOutPanel'
];

export default ModalServiceFn;



// WEBPACK FOOTER //
// ./src/services/modalService.js